//@flow
import FunctionStore from '../Base/functionStoreClass';
import { apiUpdateState, updateState, setFlagProp, getFlagProp } from '../Base/storeUtils';
import Constants from '../../constants';
import OrderCollection from './orderCollection';
import OrderModel from './orderModel';
import APIInterface from '../../APIInterfaces/order';
import CustomerStore from '../Customer/customer';
import { toImmutable } from '../../Utils/immutable';

import type { HandlerAdd, StoreState } from '../Base/functionStoreClass';
import type { OrderObject } from './orderModel';
import type { OrderParams } from '../../APIInterfaces/order';
/**
Note: Here we are exporting the OrderStore class itself, not its singleton.
To use the OrderStore, you must import orderSingleton.js.
*/

export default class OrderStore extends FunctionStore {
  /* The flags that the order store uses are :
    completedOrder: signals to the App that we can move to checkout/success

    ccValidationError: Tells the app to open error modals, happens on order failure
  */

  /*
    Returns an orders collection (a new instance everytime). If the store
    has not synced with the backend, it make use the API to get orders.
    Must be logged in to get orders.
  */
  getOrders():OrderCollection {
    return this._resolvePropertyObject('orders', OrderCollection, APIInterface.getOrders);
  }

  getOrder(orderId?:?string):OrderModel {
    const loadOrder = () => orderId && APIInterface.getOrder(orderId);
    return this._resolvePropertyObject('order', OrderModel, loadOrder);
  }

  getCompletedOrder():boolean {
    return this._getFlag('completedOrder');
  }

  getCcValidationError():boolean {
    return this._getFlag('ccValidationError');
  }

  _setup(onSuccess:HandlerAdd, onError:HandlerAdd) {
    onSuccess(Constants.CREATE_ORDER, updateState(_createOrder), [CustomerStore]);
    onSuccess(Constants.CREATE_ORDER_SUCCESS, apiUpdateState(_createOrderSuccess));
    onSuccess(Constants.GET_ORDERS_SUCCESS, apiUpdateState(_getOrdersSuccess));
    onSuccess(Constants.GET_ORDER_SUCCESS, apiUpdateState(_getOrderSuccess));
    onSuccess(Constants.ADD_CREDIT_CARD_SUCCESS, updateState(_resetErrors));
    onError(Constants.CREATE_ORDER_SUCCESS, updateState(_handleError));
  }
}

// --- Helper functions --
function _handleError(state:StoreState) {
  setFlagProp(state, 'creatingOrder', false);
  setFlagProp(state, 'ccValidationError', true);
}

// --- Response functions ---
function _createOrder(state:StoreState, params:OrderParams) {
  if (getFlagProp(state, 'creatingOrder')) return;
  setFlagProp(state, 'creatingOrder', true);
  _resetErrors(state);

  // Pass Friendbuy data
  const fbVisitor = CustomerStore.getFriendBuyVisitor();
  if (fbVisitor.getStatus() === "success") {
    params['friendbuy_visitor'] = fbVisitor.attributes;
  }

  APIInterface.createOrder(params);
}

function _createOrderSuccess(state:StoreState, order:OrderObject) {
  state.set('order', toImmutable(order));
  setFlagProp(state, 'creatingOrder', false);
  setFlagProp(state, 'completedOrder', true);
}

function _getOrdersSuccess(state:StoreState, orders:OrderObject[]) {
  state.set('orders', toImmutable(orders));
}

function _getOrderSuccess(state:StoreState, order:OrderObject) {
  state.set('order', toImmutable(order));
}

function _resetErrors(state:StoreState) {
  setFlagProp(state, 'ccValidationError', false);
}
